import React, { useMemo, useState } from 'react';
import './AdminInviteUser.scss';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { LOGIN } from '../../../../../shared/routes/path';
import { inviteUserByAdmin } from '../../../../../shared/services/admin/users/AdminUsersService';
import { Dropdown } from 'primereact/dropdown';
import { UserTeamData } from '../../../../settings/team/models/TeamResponse';

interface AdminRegisterUserProps {
  accountId: number;
  afterSave: () => void;
}

const AdminInviteUser: React.FC<AdminRegisterUserProps> = ({ accountId, afterSave }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [role, setRole] = useState<{ name: string; type: string } | null>(null);

  const roleOptions = useMemo(
    () => [
      { name: t('admin'), type: 'ADMIN' },
      { name: t('user'), type: 'USER' },
    ],
    []
  );

  const itemTemplate = (option: any) => {
    return <div className="p-dropdown-item">{option.name}</div>;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [apiError, setApiError] = useState<string | null>(null);

  const onSubmit = async (formData: any) => {
    if (!role) {
      setApiError('No role');
      return;
    }

    const data = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      role: role.type,
    } as UserTeamData;

    await inviteUserByAdmin(accountId, data)
      .then(() => {
        afterSave();
      })
      .catch((err) => {
        if (err.statusCode === 401) {
          navigate(LOGIN);
          return;
        }

        setApiError(err.response.data.error);
      });
  };

  const clearApiError = () => {
    setApiError(null);
  };

  return (
    <div className="">
      <form onSubmit={handleSubmit(onSubmit)} className="form-box__form">
        <div className="form-box__row form-box__row--with-col">
          <div className="form-box__col">
            <div className="form-box__label">{t('firstName')}:</div>
            <InputText
              onInput={clearApiError}
              {...register('firstName', { required: t('fieldIsRequired') })}
              className={errors.firstName || apiError ? 'form-box__invalid' : ''}
            />
            {errors.firstName && <span>{errors.firstName.message as string}</span>}
          </div>
          <div className="form-box__col">
            <div className="form-box__label">{t('lastName')}:</div>
            <InputText
              onInput={clearApiError}
              {...register('lastName', { required: t('fieldIsRequired') })}
              className={errors.lastName || apiError ? 'form-box__invalid' : ''}
            />
            {errors.lastName && <span>{errors.lastName.message as string}</span>}
          </div>
        </div>

        <div className="form-box__row">
          <div className="form-box__label">{t('emailAddress')}:</div>
          <InputText
            onInput={clearApiError}
            {...register('email', {
              required: t('fieldIsRequired'),
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: t('wrongEmailAddress'),
              },
            })}
            className={errors.email || apiError ? 'form-box__invalid' : ''}
          />
          {errors.email && <span>{errors.email.message as string}</span>}
        </div>

        <div className="form-box__row">
          <div className="form-box__label">{t('role')}:</div>
          <Dropdown
            value={role}
            placeholder={t('selectRole')}
            onChange={(e) => setRole(e.value)}
            options={roleOptions}
            className="dropdown"
            optionLabel="name"
            itemTemplate={itemTemplate}
          />
        </div>

        <Button className="btn btn--sm" label="Invite user" type="submit" />
      </form>
    </div>
  );
};

export default AdminInviteUser;
